import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    InputLabel,
    Link,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import PostService from '../../services/PostService';
import TaskTrackingService, { taskErrorMessage } from '../../services/TaskTrackingService';
import { flatLangOptions, groupedLangOptions } from '../subtitles/SelectLanguageComponent';
import { confirmDiscard, withAccessConfirmation } from './confirmAccess';
import { isWebLink, platformChoices, useTaskCategories, useTaskPlatforms } from './taskFields';
import VideoPicker from './VideoPicker';

const ENGLISH = 'eng';
const INSTAGRAM = 'INSTAGRAM_FACEBOOK';

// All languages is the app's Primary and Secondary groups
const ALL_OPTIONS = groupedLangOptions
    .filter((g) => g.label === 'Primary Languages' || g.label === 'Secondary Languages')
    .flatMap((g) => g.options)
    .map(({ value, label }) => ({ value, label }));

const allLanguages = (platform) => ALL_OPTIONS.filter((o) => platform !== INSTAGRAM || o.value !== ENGLISH);

const SHARED_STAGES = [
    { key: 'DUBBING', label: 'Dubbing' },
    { key: 'REPLICATION', label: 'Replication' },
    { key: 'QC', label: 'QC' },
    { key: 'FINAL_CHECK', label: 'Final check' },
];

const blank = {
    title: '',
    postType: 'POST',
    platform: '',
    sourceUrl: '',
    categoryKey: '',
    dubbing: false,
    englishDesign: '',
    englishCaption: '',
    languages: [],
    targetDate: '',
    translationDueDate: '',
    video: null,
};

const email = (user) => user?.email || '';

export const PersonPicker = ({ users, value, onChange, label, filter }) => {
    const options = filter ? users.filter(filter) : users;
    return (
        <Autocomplete
            size="small"
            options={options}
            value={users.find((u) => u.email === value) || null}
            getOptionLabel={(u) => u.name || u.email}
            isOptionEqualToValue={(a, b) => a.email === b.email}
            onChange={(_, u) => onChange(email(u))}
            renderInput={(params) => <TextField {...params} label={label} />}
        />
    );
};

/**
 * New post: one task per language, or nothing. Every picker starts as whoever had that stage and
 * language on this PM's last post, so the table is usually already right. Create and add another
 * keeps what the next post shares, since 5 or 6 are added together.
 */
const NewPostDialog = ({ open, onClose, onCreated, me }) => {
    useTrackUsage('NewPostDialog', 'dialog', 'posts', open);
    const [form, setForm] = useState(blank);
    const [users, setUsers] = useState([]);
    // language key to stage key to person
    const [people, setPeople] = useState({});
    const [perLanguage, setPerLanguage] = useState({});
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [picking, setPicking] = useState(false);
    const [added, setAdded] = useState(0);
    const categories = useTaskCategories();
    const platforms = useTaskPlatforms();
    const content = useRef(null);
    const titleBox = useRef(null);

    useEffect(() => {
        if (!open) return;
        setForm(blank);
        setPeople({});
        setPerLanguage({});
        setError(null);
        setPicking(false);
        setAdded(0);
        TaskTrackingService.assignableUsers('POST')
            .then(setUsers)
            .catch((e) => setError(taskErrorMessage(e)));
    }, [open]);

    const set = (name, value) => setForm((f) => ({ ...f, [name]: value }));
    const keys = useMemo(() => form.languages.map((l) => l.value).join(','), [form.languages]);

    // Fills in languages not seen yet; what the PM has already picked stays
    useEffect(() => {
        if (!open || form.languages.length === 0) return;
        let alive = true;
        PostService.lastAssignees(form.languages.map((l) => l.value))
            .then((last) => {
                if (!alive) return;
                setPeople((current) => {
                    const next = { ...current };
                    form.languages.forEach((l) => {
                        const was = last[l.value] || {};
                        next[l.value] = { ...was, FINAL_CHECK: was.FINAL_CHECK || me || '', ...(current[l.value] || {}) };
                    });
                    return next;
                });
            })
            .catch(() => {});
        return () => {
            alive = false;
        };
    }, [keys, open]); // eslint-disable-line react-hooks/exhaustive-deps

    const setPerson = (language, stage, value) =>
        setPeople((p) => ({ ...p, [language]: { ...(p[language] || {}), [stage]: value } }));
    const setForAll = (stage, value) =>
        setPeople((p) => {
            const next = { ...p };
            form.languages.forEach((l) => {
                next[l.value] = { ...(next[l.value] || {}), [stage]: value };
            });
            return next;
        });

    const pickPlatform = (platform) => {
        setForm((f) => ({
            ...f,
            platform,
            languages: platform === INSTAGRAM ? f.languages.filter((l) => l.value !== ENGLISH) : f.languages,
        }));
    };

    const all = allLanguages(form.platform);
    const everyLanguagePicked = all.every((o) => form.languages.some((l) => l.value === o.value));
    const pickable = flatLangOptions
        .map(({ value, label }) => ({ value, label: String(label) }))
        .filter((o) => form.platform !== INSTAGRAM || o.value !== ENGLISH);

    // Dubbing starts as the Replication person until it's changed
    const personFor = (language, stage) => {
        const row = people[language] || {};
        return (stage === 'DUBBING' ? row.DUBBING ?? row.REPLICATION : row[stage]) || '';
    };
    const stagePerson = (stage) => form.languages.map((l) => personFor(l.value, stage)).find(Boolean) || '';
    // The last post's people can differ by language, so one picker would hide some of them
    const mixed = (stage) => new Set(form.languages.map((l) => personFor(l.value, stage))).size > 1;
    const assigneesFor = (language) => {
        const row = { ...(people[language] || {}) };
        if (form.dubbing) row.DUBBING = personFor(language, 'DUBBING');
        return row;
    };
    const showPerLanguage = (stage) => perLanguage[stage] || mixed(stage);
    const togglePerLanguage = (stage) => {
        if (showPerLanguage(stage)) setForAll(stage, stagePerson(stage));
        setPerLanguage((x) => ({ ...x, [stage]: !showPerLanguage(stage) }));
    };

    const problem = () => {
        if (!form.title.trim()) return 'Give the post a title';
        if (!form.platform) return 'Pick a platform';
        if (form.languages.length === 0) return 'Pick at least one language';
        if (!form.video && !form.englishDesign.trim() && !form.englishCaption.trim()) return 'Add the English Text on design or Caption';
        if (form.sourceUrl.trim() && !isWebLink(form.sourceUrl)) return 'Paste the full source link, starting with https://';
        return null;
    };

    const submit = async (again) => {
        const stop = problem();
        if (stop) {
            setError(stop);
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const { video, ...fields } = form;
            const created = await withAccessConfirmation((grant) =>
                PostService.create({
                    ...fields,
                    title: form.title.trim(),
                    videoId: video?.videoId || null,
                    grantContentAccess: grant,
                    languages: form.languages.map((l) => l.value),
                    targetDate: form.targetDate || null,
                    translationDueDate: form.translationDueDate || null,
                    assignees: Object.fromEntries(form.languages.map((l) => [l.value, assigneesFor(l.value)])),
                }),
            );
            if (!created) return;
            onCreated && onCreated(created);
            if (again) {
                setForm((f) => ({
                    ...blank,
                    postType: f.postType,
                    platform: f.platform,
                    categoryKey: f.categoryKey,
                    languages: f.languages,
                    targetDate: f.targetDate,
                    translationDueDate: f.translationDueDate,
                }));
                setAdded((n) => n + 1);
                if (content.current) content.current.scrollTop = 0;
                titleBox.current?.focus();
            } else {
                onClose();
            }
        } catch (e) {
            setError(taskErrorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const changed =
        !!form.title.trim() ||
        !!form.sourceUrl.trim() ||
        !!form.englishDesign.trim() ||
        !!form.englishCaption.trim() ||
        !!form.video;
    const close = async () => {
        if (!changed || (await confirmDiscard('Discard this post?'))) onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (busy || (reason === 'backdropClick' && changed)) return;
                close();
            }}
            maxWidth="md"
            fullWidth
            disableEnforceFocus
        >
            <DialogTitle>New post</DialogTitle>
            <DialogContent ref={content}>
                {added > 0 && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {added} post{added === 1 ? '' : 's'} added. Add the next one.
                    </Alert>
                )}
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, mt: 1 }}>
                    <TextField
                        label="Title"
                        required
                        size="small"
                        autoFocus
                        inputRef={titleBox}
                        value={form.title}
                        onChange={(e) => set('title', e.target.value)}
                        sx={{ gridColumn: '1 / -1' }}
                    />
                    <FormControl>
                        <FormLabel id="post-format">Format</FormLabel>
                        <RadioGroup row aria-labelledby="post-format" value={form.postType} onChange={(e) => set('postType', e.target.value)}>
                            <FormControlLabel value="POST" control={<Radio size="small" />} label="Post" />
                            <FormControlLabel value="STORY" control={<Radio size="small" />} label="Story" />
                        </RadioGroup>
                    </FormControl>
                    <FormControl size="small" required>
                        <InputLabel id="post-platform">Platform</InputLabel>
                        <Select labelId="post-platform" label="Platform" value={form.platform} onChange={(e) => pickPlatform(e.target.value)}>
                            {platformChoices(platforms, form.platform).map((p) => (
                                <MenuItem key={p.value} value={p.value}>
                                    {p.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Source link (optional)"
                        placeholder="https://"
                        size="small"
                        value={form.sourceUrl}
                        onChange={(e) => set('sourceUrl', e.target.value)}
                    />
                    <FormControl size="small">
                        <InputLabel id="post-bucket">Bucket</InputLabel>
                        <Select labelId="post-bucket" label="Bucket" value={form.categoryKey} onChange={(e) => set('categoryKey', e.target.value)}>
                            <MenuItem value="">None</MenuItem>
                            {categories
                                .filter((c) => !c.hidden)
                                .map((c) => (
                                    <MenuItem key={c.key} value={c.key}>
                                        {c.label}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                        <VideoPicker value={form.video} onChange={(v) => set('video', v)} languages={form.languages} />
                    </Box>
                    {!form.video && (
                        <TextField
                            label="English Text on design"
                            multiline
                            minRows={4}
                            value={form.englishDesign}
                            onChange={(e) => set('englishDesign', e.target.value)}
                            helperText="One line per slide"
                        />
                    )}
                    <TextField
                        label="English Caption"
                        multiline
                        minRows={4}
                        value={form.englishCaption}
                        onChange={(e) => set('englishCaption', e.target.value)}
                        helperText="With its hashtags"
                    />
                    <FormControlLabel
                        control={<Checkbox checked={form.dubbing} onChange={(e) => set('dubbing', e.target.checked)} />}
                        label="Dubbing"
                        sx={{ gridColumn: '1 / -1' }}
                    />
                    <Box sx={{ gridColumn: '1 / -1' }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                Languages
                            </Typography>
                            <Button
                                size="small"
                                variant={everyLanguagePicked ? 'contained' : 'outlined'}
                                onClick={() => set('languages', everyLanguagePicked ? [] : all)}
                            >
                                All languages ({all.length})
                            </Button>
                            <Button size="small" onClick={() => setPicking((x) => !x)} aria-expanded={picking}>
                                Pick languages
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                                {form.languages.length} picked
                                {form.platform === INSTAGRAM ? '. Instagram and Facebook have no English.' : ''}
                            </Typography>
                        </Box>
                        {(picking || (form.languages.length > 0 && !everyLanguagePicked)) && (
                            <Autocomplete
                                multiple
                                size="small"
                                options={pickable}
                                value={form.languages}
                                getOptionLabel={(o) => o.label}
                                isOptionEqualToValue={(a, b) => a.value === b.value}
                                onChange={(_, v) => set('languages', v)}
                                renderInput={(params) => <TextField {...params} label="Languages" />}
                            />
                        )}
                    </Box>
                    <TextField
                        label="Posting date (optional)"
                        type="date"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={form.targetDate}
                        onChange={(e) => set('targetDate', e.target.value)}
                    />
                    <TextField
                        label="Translation due date"
                        type="date"
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        value={form.translationDueDate}
                        onChange={(e) => set('translationDueDate', e.target.value)}
                    />
                </Box>

                {form.languages.length > 0 && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            Who does each stage
                        </Typography>
                        <Table size="small" aria-label="Translators per language" sx={{ mb: 2 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Language</TableCell>
                                    <TableCell>Translate text</TableCell>
                                    <TableCell>Text QC</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {form.languages
                                    .filter((l) => l.value !== ENGLISH)
                                    .map((l) => (
                                        <TableRow key={l.value}>
                                            <TableCell>{l.label}</TableCell>
                                            {['TRANSLATE_TEXT', 'TEXT_QC'].map((stage) => (
                                                <TableCell key={stage} sx={{ minWidth: 200 }}>
                                                    <PersonPicker
                                                        users={users}
                                                        label={`${l.label} ${stage === 'TEXT_QC' ? 'Text QC' : 'Translate text'}`}
                                                        value={people[l.value]?.[stage] || ''}
                                                        onChange={(v) => setPerson(l.value, stage, v)}
                                                    />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                            {SHARED_STAGES.filter((s) => s.key !== 'DUBBING' || form.dubbing).map((s) => (
                                <Box key={s.key}>
                                    {showPerLanguage(s.key) ? (
                                        form.languages.map((l) => (
                                            <Box key={l.value} sx={{ mb: 1 }}>
                                                <PersonPicker
                                                    users={users}
                                                    label={`${s.label}: ${l.label}`}
                                                    value={personFor(l.value, s.key)}
                                                    onChange={(v) => setPerson(l.value, s.key, v)}
                                                    filter={s.key === 'FINAL_CHECK' ? (u) => u.projectManager : undefined}
                                                />
                                            </Box>
                                        ))
                                    ) : (
                                        <PersonPicker
                                            users={users}
                                            label={s.label}
                                            value={stagePerson(s.key)}
                                            onChange={(v) => setForAll(s.key, v)}
                                            filter={s.key === 'FINAL_CHECK' ? (u) => u.projectManager : undefined}
                                        />
                                    )}
                                    {form.languages.length > 1 && (
                                        <Link
                                            component="button"
                                            type="button"
                                            variant="caption"
                                            onClick={() => togglePerLanguage(s.key)}
                                        >
                                            {showPerLanguage(s.key) ? 'Same for every language' : 'Different per language'}
                                        </Link>
                                    )}
                                </Box>
                            ))}
                        </Box>
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                            A stage left empty waits for a person.
                        </Typography>
                    </>
                )}
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={close} disabled={busy}>
                    Cancel
                </Button>
                <Button onClick={() => submit(true)} disabled={busy}>
                    Create and add another
                </Button>
                <Button variant="contained" onClick={() => submit(false)} disabled={busy}>
                    Create post
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NewPostDialog;
